"use client"

import { useEffect, useMemo, useState } from "react"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { sendGTMEvent } from "@next/third-parties/google"

import { generatePageTypeTrackingData } from "@/lib/gtmTrackingHelper"
import { BasketLineItemData, BasketTotalData } from "@/lib/storefront/basket/basket"
import { determineBasketTotalData } from "@/lib/storefront/basket/basketHelper"
import {
  calculateCategoryPathString,
  determineVariableAttributeValuesAsString,
} from "@/lib/storefront/product/productHelper"
import { CommonPageContext } from "@/lib/tracking/tracking"
import { buildGa4Item, resetEcommerce, sendGTMEventIfAnalyticsAllowed, toCustomerType } from "@/lib/ga4TrackingHelper"

export interface BasketPageTrackingClientProps {
  commonPageContext: CommonPageContext
  page_type: string
}

function generateViewCartGa4(
  basketTotalData: BasketTotalData | undefined,
  basketLineItems: BasketLineItemData[] | undefined,
  isAnonymous: boolean,
  isB2B: boolean
) {
  if (basketLineItems && basketTotalData) {
    const currency = basketTotalData.currency || "EUR"
    const items = (basketLineItems ?? []).map((li) =>
      buildGa4Item({
        product: li.product,
        priceGross: li.singleSalePriceGross ?? (li.totalSalePriceGross ?? 0) / (li.quantity || 1),
        currency,
        quantity: li.quantity,
        variant: determineVariableAttributeValuesAsString(li.product),
        categoryPath: calculateCategoryPathString(li.product),
      })
    )
    const value = Number(
      basketLineItems
        .reduce((sum, li) => {
          const lineValue = li.totalSalePriceGross ?? (li.singleSalePriceGross ?? 0) * (li.quantity || 1)
          return sum + lineValue
        }, 0)
        .toFixed(2)
    )

    const customer_type = toCustomerType(isAnonymous, isB2B)
    return {
      event: "view_cart",
      pagetyp: "cart",
      customer_type,
      ecommerce: {
        currency,
        value,
        items,
      },
    }
  }
  return undefined
}

export default function BasketPageTrackingClient(props: BasketPageTrackingClientProps) {
  const [pageTracked, setPageTracked] = useState(false)
  const [cartViewTracked, setCartViewTracked] = useState(false)
  const { basketData, basketLineItems, basketPromotionCodes, usedGuidedSelling, guidedSellingCampaign } =
    useUbStorefrontState()

  const isB2B = props.commonPageContext.isB2B
  const isAnonymous = props.commonPageContext.isAnonymous
  const isoLocale = props.commonPageContext.isoLocale

  const basketTotalData: BasketTotalData = useMemo(() => {
    return determineBasketTotalData(isB2B, basketData, basketLineItems, basketPromotionCodes, isAnonymous, isoLocale)
  }, [basketData, basketLineItems, basketPromotionCodes, isAnonymous, isB2B, isoLocale])

  useEffect(() => {
    if (!pageTracked) {
      sendGTMEvent(
        generatePageTypeTrackingData(
          props.commonPageContext.isAnonymous,
          props.commonPageContext.isB2B,
          props.page_type,
          props.commonPageContext.userId,
          undefined,
          undefined,
          usedGuidedSelling,
          guidedSellingCampaign
        )
      )

      /*Track bing event*/
      if (window.uetq) {
        window.uetq.push("event", "page_view", { page_path: window.location.pathname })
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPageTracked(true)
    }
  }, [props.commonPageContext, props.page_type, pageTracked, usedGuidedSelling, guidedSellingCampaign])

  useEffect(() => {
    if (!cartViewTracked && basketLineItems && basketTotalData) {
      const payload = generateViewCartGa4(basketTotalData, basketLineItems, isAnonymous, isB2B)
      if (!payload) return
      sendGTMEventIfAnalyticsAllowed(resetEcommerce())
      sendGTMEventIfAnalyticsAllowed(payload)
      sendGTMEventIfAnalyticsAllowed(resetEcommerce())

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCartViewTracked(true)
    }
  }, [props.commonPageContext, cartViewTracked, basketTotalData, basketLineItems, isAnonymous, isB2B])

  // eslint-disable-next-line better-tailwindcss/no-unknown-classes
  return <span className="PageType hidden">{props.page_type}</span>
}

/*
▲ {
  "event": "cart_view",
    "ecommerce": ▲ {
    "currencyCode": "EUR",
      "value": "377.1",
      "cart_view": ▲ {
      "products": ▲ [
                   ▲ {
        "name": "UDOBÄR Fahrradständer",
          "id": "A275672",
          "price": "377.1",
          "brand": "UDOB&Auml;R",
          "category": "Betrieb/Hallenanlagen und Aussenanlagen/Aussenmobiliar/Fahrradständer",
          "variant": "6/",
          "quantity": 1
      }
    ]
    }
  },
  "gtm.uniqueEventId": 26
}*/
